import {Dispatch, SetStateAction} from 'react'
import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
  RequestResult,
} from '../../lib'

import {doREQUEST_ERROR, doREQUEST_COMPLETE, doMETHOD_UPDATE_QUESTIONNAIRE, doMETHOD_UPDATE_TASK} from '../state'
import {getToken} from './token_fetcher'
import {
  createDisplayStrategyUpsertListMap as createDisplayStrategyUpsertItemMapList,
  parseDisplayStrategyUpsertListFromMap as parseDisplayStrategyUpsertItemMapList,
  QuestionnaireTempSection,
  sanitizeQuestionnaireForUpdate,
  TempQuestionnaireDisplayStrategyUpsertItem,
} from '../../model'
import {IQuestionnaire} from '../../shared/db/questionnaire'

export const REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE =
  'REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE'
export const doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE = createDoRequestAction(
  REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
)

export const requestTaskQuestionnaireWithDisplayStrategyUpdateActionCreators = {
  doREQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
}

interface Payload {
  payload: {
    id: string
    requestId: string
    projectId: string
    methodId: string
    workspaceId: string
    type: string
    scheduleRepeat: boolean
    scheduleDescription: string
    questionnaire: {[key: string]: any}
    color: string
    reminderList?: Record<string, string>[]
    displayStrategyList?: TempQuestionnaireDisplayStrategyUpsertItem[]
  }
  setRequestResult?: Dispatch<SetStateAction<RequestResult | null>>
}

export function* requestTaskQuestionnaireWithDisplayStrategyUpdate({payload, setRequestResult}: Payload): SagaIterator {
  debug('saga*requestTaskQuestionnaireWithDisplayStrategyUpdate')

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        id: v.string().uuid().exist(),
        requestId: v.string().uuid().exist(),
        projectId: v.string().uuid().exist(),
        methodId: v.string().uuid().exist(),
        workspaceId: v.string().uuid().exist(),
        type: v.string().exist(),
        scheduleRepeat: v.boolean().optional(),
        scheduleDescription: v.string().optional(),
        questionnaire: v.object(),
        color: v.string().optional(),
        reminderList: v.array().optional(),
        displayStrategyList: v.array().optional(),
      }),
    })
  } catch (error) {
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    )
  }

  const {
    scheduleRepeat,
    scheduleDescription,
    id,
    type,
    questionnaire,
    projectId,
    workspaceId,
    methodId,
    color,
    reminderList,
    displayStrategyList,
  } = payload

  const accessToken = yield call(getToken)

  const sanitizedQuestionnaire = sanitizeQuestionnaireForUpdate(questionnaire as IQuestionnaire)
  const updateTaskResult = yield request({
    method: 'post',
    url: `${API_URL}/v1/web/task-questionnaire-update`,
    accessToken,
    data: {
      scheduleRepeat,
      scheduleDescription,
      id,
      type,
      questionnaire: sanitizedQuestionnaire,
      projectId,
      workspaceId,
      methodId,
      color,
      required: false,
      reminderList,
    },
  })

  if (!updateTaskResult.success || updateTaskResult.error) {
    const error = updateTaskResult.error || new Error('request did not succeed')
    debug(error)
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
          fromPayload: payload,
          requestId: payload.requestId,
          url: '/v1/web/task-questionnaire-update',
          ...updateTaskResult,
          error,
        },
        setRequestResult,
      ),
    )
  }

  delete updateTaskResult.payload.enabled
  yield put(doMETHOD_UPDATE_TASK(updateTaskResult.payload))

  // create display strategy if provided
  let displayStrategyResult = {}
  if (displayStrategyList?.length) {
    const displayStrategyUpsertItemMapList = createDisplayStrategyUpsertItemMapList(
      questionnaire.sectionList, //tempQuestionnaireSectionList
      displayStrategyList,
    )

    const newQuestionnaire = updateTaskResult.payload.questionnaire
    const upsertDisplayStrategyResult = yield request({
      method: 'post',
      url: `${API_URL}/v1/web/questionnaire-display-strategy-upsert`,
      accessToken,
      data: {
        questionnaireId: newQuestionnaire.id,
        displayStrategyList: parseDisplayStrategyUpsertItemMapList(
          newQuestionnaire.sectionList,
          displayStrategyUpsertItemMapList,
        ),
      },
    })

    if (!upsertDisplayStrategyResult.success || upsertDisplayStrategyResult.error) {
      const error = upsertDisplayStrategyResult.error || new Error('request did not succeed')
      return yield put(
        doREQUEST_ERROR(
          {
            fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
            requestId: payload.requestId,
            error,
          },
          setRequestResult,
        ),
      )
    }

    yield put(
      doMETHOD_UPDATE_QUESTIONNAIRE({
        ...upsertDisplayStrategyResult.payload,
      }),
    )

    displayStrategyResult = {...upsertDisplayStrategyResult.payload}
  }

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE,
        requestId: payload.requestId,
        ...updateTaskResult,
        upsertDisplayStrategyResult: displayStrategyResult,
      },
      setRequestResult,
    ),
  )
}

export function* taskQuestionnaireWithDisplayStrategyUpdateSaga() {
  yield takeEvery(
    REQUEST_TASK_QUESTIONNAIRE_WITH_DISPLAY_STRATEGY_UPDATE as any,
    requestTaskQuestionnaireWithDisplayStrategyUpdate,
  )
}
